import { faPencil, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import { useContext, useMemo, useState } from 'react';

import { ssnSchema } from '@/components/SSN/ssnSchema';
import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import CreateSecretForm from '@/forms/SecretForm/CreateSecretForm';
import UpdateSecretForm from '@/forms/SecretForm/UpdateSecretForm';
import useSecret from '@/hooks/useSecret';
import PersonContext from '@/pages/Person/PersonContext';
import Button from '@/ui/components/Button';
import DataList, { DataRow } from '@/ui/components/DataList';
import Spinner from '@/ui/components/Spinner';
import { hasPermission, PEOPLE_CHANGE_SECRET, PEOPLE_VIEW_SECRET } from '@/utils/userPermission';

const SSN = () => {
  const { permissions } = useContext(AppContext);
  const {
    person: { id: personId }
  } = useContext(PersonContext);
  const queryClient = useQueryClient();
  const [decrypt, setDecrypt] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const canView = hasPermission({ requiredPermission: PEOPLE_VIEW_SECRET, permissions });
  const canEdit = hasPermission({ requiredPermission: PEOPLE_CHANGE_SECRET, permissions });

  const params = {
    entity_id: personId,
    entity_type: 'person',
    entity_type_app_label: 'people',
    secret_type_slug: 'ssn'
  };

  const { data, isPending: isPendingSSNFetch, isRefetching } = useSecret({ decrypt, params });
  const formattedSsnValue = useMemo(() => {
    return data?.value ? data.value.replace(/(\d{3})(\d{2})(\d{4})/, '$1-$2-$3') : '';
  }, [data?.value]);

  const toggleReveal = () => {
    setDecrypt((val) => !val);
  };

  const handleEdit = async () => {
    if (data?.value) {
      setDecrypt(true);
    }

    setIsEditing(true);
  };

  const onSuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: ['getSecret', decrypt, params] });
    setIsEditing(false);
  };

  const renderSSN = () => {
    if (isPendingSSNFetch || isRefetching) {
      return <Spinner />;
    }

    if (data?.value) {
      return (
        <div className="flex items-center">
          {formattedSsnValue}

          {canView && (
            <Button
              ariaLabel={decrypt ? 'Hide' : 'Reveal'}
              className="ml-2"
              color="white"
              onClick={toggleReveal}
              size="xs">
              <FontAwesomeIcon icon={decrypt ? faEyeSlash : faEye} />
            </Button>
          )}
        </div>
      );
    }

    return <>No SSN on record</>;
  };

  if (isEditing) {
    if (isRefetching) {
      return <Spinner />;
    }

    if (!isRefetching && data?.value) {
      return (
        <div>
          <UpdateSecretForm
            initialValues={{ ssn: data?.value ?? '' }}
            onSuccess={onSuccess}
            schema={ssnSchema}
            secret={{
              ...data,
              entity_type_app_label: 'people',
              secret_type_slug: data.secret_type.slug
            }}
          />
          <Button color={Color.WHITE} onClick={() => setIsEditing(false)}>
            Cancel
          </Button>
        </div>
      );
    }

    return (
      <div>
        <CreateSecretForm
          entity_id={personId}
          entity_type="people.person"
          initialValues={{ ssn: '' }}
          onSuccess={onSuccess}
          schema={ssnSchema}
          secret_type_slug="ssn"
        />
        <Button color={Color.WHITE} onClick={() => setIsEditing(false)}>
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <div className="relative group/subsection">
      <DataList>
        <DataRow label="Social Security Number">{renderSSN()}</DataRow>
        {/*// TODO: componentize this edit button*/}
        {canEdit && (
          <Button
            ariaLabel="Edit"
            className="absolute top-4 right-0 group-hover/section:visible invisible"
            color="white"
            disabled={isRefetching}
            onClick={handleEdit}
            size="xs">
            <FontAwesomeIcon icon={faPencil} />
          </Button>
        )}
      </DataList>
    </div>
  );
};

export default SSN;
